import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * A searchable single-select combobox with optional recent-selection memory.
 *
 * Props:
 *   - value / onChange(value): controlled selected string value.
 *   - options: [{ value, label?, sublabel? }] — the selectable list.
 *   - onSearch(term): optional async callback for SERVER-side search. When set,
 *       the component debounces keystrokes and calls it, expecting the parent to
 *       refresh `options`; client-side filtering is then skipped. It is also
 *       called with '' when the dropdown first opens (to preload results).
 *   - recentsKey: localStorage key. When set, selections are remembered and shown
 *       at the top of the list while the search box is empty.
 *   - allowFreeText: when true (default), the typed term is usable as the value
 *       even if it doesn't match an option (so a brand-new Case number works).
 *   - loading, disabled, placeholder, emptyHint: UI state/labels.
 *
 * This follows the Lyric convention that selector inputs (JIRA/GitHub/Salesforce)
 * are searchable pickers, never raw text-entry inputs.
 */

const MAX_RECENTS = 8;

function loadRecents(key) {
  if (!key || typeof window === 'undefined' || !window.localStorage) return [];
  try {
    const arr = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(arr) ? arr.filter((r) => r && r.value).slice(0, MAX_RECENTS) : [];
  } catch {
    return [];
  }
}

function pushRecent(key, entry) {
  if (!key || typeof window === 'undefined' || !window.localStorage || !entry || !entry.value) return;
  try {
    const rest = loadRecents(key).filter((r) => r.value !== entry.value);
    const next = [{ value: entry.value, label: entry.label, sublabel: entry.sublabel }, ...rest]
      .slice(0, MAX_RECENTS);
    window.localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* ignore quota / serialization errors */
  }
}

export default function SearchableSelect({
  value = '',
  onChange,
  options = [],
  onSearch,
  recentsKey,
  allowFreeText = true,
  loading = false,
  disabled = false,
  placeholder = 'Search…',
  emptyHint = 'No matches',
}) {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [recents, setRecents] = useState(() => loadRecents(recentsKey));
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const menuId = useRef(`ss-menu-${Math.random().toString(36).slice(2)}`).current;

  // Close on outside click.
  useEffect(() => {
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced server-side search (and an initial load when the menu opens).
  useEffect(() => {
    if (!onSearch || !open) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { onSearch(term.trim()); }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [term, onSearch, open]);

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t || onSearch) return options; // server already filtered when onSearch is set
    return options.filter((o) => (
      String(o.value).toLowerCase().includes(t)
      || String(o.label || '').toLowerCase().includes(t)
      || String(o.sublabel || '').toLowerCase().includes(t)
    ));
  }, [options, term, onSearch]);

  const showRecents = Boolean(recentsKey) && !term.trim() && recents.length > 0;

  const rows = useMemo(() => {
    if (showRecents) {
      const recentValues = new Set(recents.map((r) => r.value));
      const rest = filtered.filter((o) => !recentValues.has(o.value));
      return [
        ...recents.map((r) => ({ ...r, recent: true })),
        ...rest,
      ];
    }
    return filtered;
  }, [showRecents, recents, filtered]);

  const select = useCallback((opt) => {
    if (!opt) return;
    onChange(opt.value);
    setTerm('');
    setOpen(false);
    if (recentsKey) {
      pushRecent(recentsKey, opt);
      setRecents(loadRecents(recentsKey));
    }
  }, [onChange, recentsKey]);

  const handleChange = useCallback((e) => {
    const v = e.target.value;
    setTerm(v);
    setOpen(true);
    setHighlight(0);
    if (allowFreeText) onChange(v); // reflect typing live so the value tracks the box
  }, [allowFreeText, onChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, Math.max(rows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && rows[highlight]) {
        e.preventDefault();
        select(rows[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [open, rows, highlight, select]);

  const clear = useCallback(() => {
    onChange('');
    setTerm('');
    setOpen(true);
  }, [onChange]);

  const inputValue = open ? term : (value || '');

  return (
    <div ref={rootRef} style={styles.root}>
      <div style={styles.inputWrap}>
        <input
          style={styles.input}
          value={inputValue}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onFocus={() => { setTerm(value || ''); setOpen(true); setHighlight(0); }}
          onBlur={(e) => {
            if (rootRef.current && (!e.relatedTarget || !rootRef.current.contains(e.relatedTarget))) {
              setOpen(false);
            }
          }}
          onKeyDown={handleKeyDown}
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={menuId}
          aria-haspopup="listbox"
          aria-activedescendant={open && rows[highlight] ? `${menuId}-opt-${highlight}` : undefined}
          role="combobox"
        />
        {(inputValue) && !disabled && (
          <button
            type="button"
            style={styles.clearBtn}
            onClick={clear}
            title="Clear"
            aria-label="Clear"
          >
            ×
          </button>
        )}
      </div>
      {open && (
        <div style={styles.menu} id={menuId} role="listbox">
          {loading && <div style={styles.menuNote}>Searching…</div>}
          {!loading && rows.length === 0 && (
            <div style={styles.menuNote}>{emptyHint}</div>
          )}
          {!loading && showRecents && recents.length > 0 && (
            <div style={styles.menuHeader}>Recent selections</div>
          )}
          {!loading && rows.map((opt, i) => {
            const isRecentHeaderBoundary = showRecents
              && opt.recent !== true
              && (i === 0 || rows[i - 1].recent === true);
            return (
              <React.Fragment key={`${opt.value}-${i}`}>
                {isRecentHeaderBoundary && <div style={styles.menuHeader}>All results</div>}
                <button
                  type="button"
                  id={`${menuId}-opt-${i}`}
                  role="option"
                  tabIndex={-1}
                  aria-selected={opt.value === value}
                  style={{ ...styles.option, ...(i === highlight ? styles.optionActive : null) }}
                  onMouseEnter={() => setHighlight(i)}
                  onMouseDown={(e) => { e.preventDefault(); select(opt); }}
                >
                  <span style={styles.optValue}>
                    {opt.recent ? '↩ ' : ''}{opt.label || opt.value}
                  </span>
                  {opt.sublabel ? <span style={styles.optSub}>{opt.sublabel}</span> : null}
                </button>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  root: { position: 'relative', width: '100%' },
  inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  input: {
    width: '100%', padding: '8px 30px 8px 10px', border: '1px solid #ccd3dc',
    borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box',
  },
  clearBtn: {
    position: 'absolute', right: '6px', background: 'transparent', border: 'none',
    color: '#8a94a6', cursor: 'pointer', fontSize: '18px', lineHeight: 1, padding: '0 4px',
  },
  menu: {
    position: 'absolute', zIndex: 30, top: 'calc(100% + 2px)', left: 0, right: 0,
    maxHeight: '260px', overflowY: 'auto', background: '#fff', border: '1px solid #ccd3dc',
    borderRadius: '6px', boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
  },
  menuNote: { padding: '10px 12px', color: '#6b7688', fontSize: '12px' },
  menuHeader: {
    padding: '6px 12px', color: '#6b7688', fontSize: '11px', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.04em', background: '#f4f6f9',
    position: 'sticky', top: 0,
  },
  option: {
    display: 'flex', flexDirection: 'column', gap: '2px', width: '100%',
    textAlign: 'left', padding: '8px 12px', background: 'transparent', border: 'none',
    borderBottom: '1px solid #f0f2f5', cursor: 'pointer',
  },
  optionActive: { background: '#eef4ff' },
  optValue: { fontSize: '13px', color: '#172b4d', fontWeight: 500 },
  optSub: { fontSize: '11px', color: '#6b7688' },
};
